import { appAxios } from "@/core/utils/appAxios";
import {
    APIResponse,
    AuthenticatedVehicleDto,
    ShoppingCartItemDto,
    CompanyDeliveryAddressDto,
    ShoppingCartDto,
    VehicleImageDto,
    ShoppingCartCheckoutSimulationItemDto,
    Promotion,
    VehicleAvailableDealDto,
} from "@/shared/api-contract/api-contract";
import { reactive } from "vue";
import { sessionModel } from "@/shared/state/session.model";
import { currentUserModel } from "@/shared/state/current-user.model";

interface IState {
    showLoading: boolean;
    transportExpeditor: number;
    totalPrice: number;
    totalDeliveryPrice: number;
    simulationTotalPrice?: number | null;
    simulationTotalPriceIncludingVat?: number | null;
    simulationVehiclesTotalPrice?: number | null;
    simulationVehiclesTotalPriceIncludingVat?: number | null;
    simulationTotalTransportCost?: number | null;
    simulationTotalTransportCostIncludingVat?: number | null;
    simulationVehiclesTotalDiscount?: number | null;
    simulationVehiclesTotalPriceWithoutDiscount?: number | null;
    simulationVehiclesTotalDiscountPercentage?: number | null;
    simulationItems?: ShoppingCartCheckoutSimulationItemDto[] | null;
    simulationTotalAdminCost: number;
    simulationSubTotalPrice: number;
    simulationTotalVAT: number;
    simulationTotalDealReduction: number;
    simulationVehiclesTotalDiscountAmount: number;
}
export class ShoppingCartModel {
    items: (ShoppingCartItemDto & { isChecked?: boolean } & {
        thumbnail_image?: VehicleImageDto;
    })[] = [];
    availableDeals: VehicleAvailableDealDto[] = [];
    state = reactive<IState>({
        showLoading: false,
        transportExpeditor: 2,
        totalPrice: 0,
        totalDeliveryPrice: 0,
        // Simulation values
        //  TotalPrice
        simulationTotalPrice: 0,
        simulationTotalPriceIncludingVat: 0,
        //  summary price of all vehicles
        simulationVehiclesTotalPrice: 0,
        simulationVehiclesTotalPriceIncludingVat: 0,
        //  Delivery price
        simulationTotalTransportCost: 0,
        simulationTotalTransportCostIncludingVat: 0,
        // Disount info
        simulationVehiclesTotalDiscount: 0,
        simulationVehiclesTotalPriceWithoutDiscount: 0,
        simulationVehiclesTotalDiscountPercentage: 0,
        // detailed price info by vehicles
        simulationItems: [],
        // missing fields
        // Admin fees: missing
        simulationTotalAdminCost: 0,
        // Subtotal: missing
        simulationSubTotalPrice: 0,
        // VAT: missing
        simulationTotalVAT: 0,
        simulationTotalDealReduction: 0,
        simulationVehiclesTotalDiscountAmount: 0,
    });
    deliveryAddresses: readonly CompanyDeliveryAddressDto[] = [];
    selectedAddress?: string;

    get isAuthenticated() {
        return !!sessionModel.sessionDto.value;
    }
    /* API Connectivity --------------------------------------------------------------------------------------------- */
    async fetchShoppingCart() {
        if (this.isAuthenticated) {
            return appAxios.value
                .get<APIResponse<ShoppingCartDto>>(`/client/web/user/shopping-cart`)
                .then((response) => {
                    if (response.status === 200 && response.data.status.code === 200) {
                        this.items = response.data.data.items;
                        this.availableDeals = response.data.data.available_deals || [];
                    }
                })
                .catch(console.log);
        }
    }

    async addVehicleToCart(id: number | string) {
        return appAxios.value
            .post<APIResponse<void>>(`/client/web/user/shopping-cart/${id}`)
            .then(async (result) => {
                if (result.status === 200 && result.data.status.code === 200) {
                    await this.fetchShoppingCart();
                }
            })
            .catch(console.log);
    }

    async removeVehicleFromCart(toRemove: AuthenticatedVehicleDto) {
        return appAxios.value
            .delete<APIResponse<undefined>>(
                `/client/web/user/shopping-cart/${toRemove.id}`,
            )
            .then(async (result) => {
                if (result.status === 200 && result.data.status.code === 200) {
                    await this.fetchShoppingCart();
                }
            })
            .catch(console.log);
    }

    async fetchDeliveryAddresses() {
        const isCompanyAvailible = currentUserModel.userDto.value?.id_company;
        if (!isCompanyAvailible) return;
        await appAxios.value
            .get<APIResponse<CompanyDeliveryAddressDto[]>>(
                "/client/web/company/delivery-addresses",
            )
            .then((response) => {
                if (response.status === 200 && response.data.status.code === 200) {
                    this.deliveryAddresses = [
                        {} as CompanyDeliveryAddressDto,
                        ...response.data.data,
                    ];
                }
            })
            .catch(console.log);
    }
}

export const cartModel = new ShoppingCartModel();
